import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { formatCop, paymentMethodLabel } from '@/lib/cccdPayments';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { cccdAdmin, type AdminPayment, type AdminTeamDetail, type AdminTeamMember, type CatalogOption, type DiscountType, type EventCategory } from '@/lib/api/cccdAdminClient';
import { TeamStatusBadge } from '@/components/cccd/TeamStatusBadge';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { TeamStatusTimeline } from '@/components/cccd/TeamStatusTimeline';
import { TeamQuickActions } from '@/components/cccd/TeamQuickActions';
import { TeamDiscountForm } from '@/components/cccd/TeamDiscountForm';
import { TeamFichaForm } from '@/components/cccd/TeamFichaForm';
import { PodiumDiscountPicker } from '@/components/cccd/PodiumDiscountPicker';
import { TeamShell, type TeamTabKey } from '@/components/cccd/TeamShell';
import { TEAM_STATUS_LABELS } from '@/lib/cccdTeamStatus';
import { slugify } from '@/lib/slugify';
import Alert from '@/components/ui/alert/Alert';
import { getErrorMessage } from '@/lib/errors';
import { inputCls, labelCls, textareaCls, StyledSelect, btnSave, btnSecondary } from '@/components/admin/FormComponents';
import { getValidationErrorMessage } from '@/lib/errors';
import { toast, toastError, toastSuccess } from '@/lib/toast';
import DataTable, { type DataTableColumn } from '@/components/ui/table/DataTable';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { AddMemberSearchBar } from '@/components/cccd/AddMemberSearchBar';
import { TourButton } from '@/components/admin/TourButton';
import { cccdTeamRosterSyncTourSteps } from '@/lib/tours/cccdTeamRosterSyncTour';
import { PublicSwitch } from '@/components/cccd/PublicProfileControls';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import { LoadState } from "@/components/ui/LoadState"

type Tab = TeamTabKey;
type InstitutionOption = { id: string; name: string };

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente de revisión',
  confirmed: 'Confirmado',
  rejected: 'Rechazado',
};

function paymentStatusClass(status: string): string {
  if (status === 'confirmed') return 'text-success-600 dark:text-success-500';
  if (status === 'rejected') return 'text-error-600 dark:text-error-500';
  if (status === 'pending') return 'text-warning-600 dark:text-orange-400';
  return 'text-gray-500';
}

const INVITE_STATUS_LABELS: Record<string, string> = {
  accepted: 'Aceptó',
  pending: 'Sin responder',
  declined: 'Rechazó',
};

function inviteStatusClass(status: string): string {
  if (status === 'accepted') return 'text-success-600 dark:text-success-500';
  if (status === 'declined') return 'text-error-600 dark:text-error-500';
  if (status === 'pending') return 'text-warning-600 dark:text-orange-400';
  return 'text-gray-500';
}

// Categoría (estructural, no editable) + Equipo (nombre/institución) +
// Responsable — elegido de la lista de Miembros (isPrimaryContact, máx. 2),
// no del usuario capitán: ese es solo la cuenta de login del equipo, sin
// endpoint para editarla, y no es lo que el admin espera cambiar aquí
// (cccd-ux-redesign). Mismo endpoint/regla que la columna "Contacto
// principal" del tab Miembros.
// cccd-team-showcase: imágenes del equipo mostradas en la galería
// competitiva de Academy — admin-only, sube/reemplaza/quita de inmediato
// (no forma parte del guardado de "Información" del equipo). El parche
// (emblema/logo del equipo) y la foto grupal (todos los integrantes juntos)
// son conceptos distintos, cada uno con su propia sección — no confundirlos.
function TeamImageUpload({
  title,
  subtitle,
  imageUrl,
  shape,
  uploadUrl: getUploadUrl,
  save,
  successLabel,
  removedLabel,
  helpText,
}: {
  title: string;
  subtitle: string;
  imageUrl: string | null;
  shape: 'circle' | 'landscape';
  uploadUrl: (file: File) => Promise<{ upload_url: string; key: string }>;
  save: (key: string | null) => Promise<unknown>;
  successLabel: string;
  removedLabel: string;
  helpText: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(file: File) {
    setUploading(true);
    setError('');
    try {
      const { upload_url, key } = await getUploadUrl(file);
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Error subiendo la imagen: ${putRes.status}`);
      await save(key);
      toastSuccess(successLabel);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al subir la imagen';
      setError(message);
      toastError(message);
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    try {
      await save(null);
      toastSuccess(removedLabel);
    } catch (err) {
      toastError(getValidationErrorMessage(err));
    }
  }

  return (
    <SectionHeader
      title={title}
      subtitle={subtitle}
      icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></svg>}
    >
      {imageUrl ? (
        <div className="flex items-center gap-4">
          <img
            src={imageUrl}
            alt={title}
            className={
              shape === 'circle'
                ? 'size-[72px] rounded-full border border-gray-200 object-cover dark:border-gray-800'
                : 'h-[90px] w-[140px] rounded-xl border border-gray-200 object-cover dark:border-gray-800'
            }
          />
          <div>
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">Imagen cargada correctamente.</p>
            <button type="button" onClick={() => void handleRemove()} className="text-[13px] font-medium text-error-600 hover:underline">
              Quitar imagen
            </button>
          </div>
        </div>
      ) : (
        <label className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-gray-300 p-5 text-center dark:border-gray-700 ${uploading ? 'opacity-60' : ''}`}>
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = '';
            }}
          />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {uploading ? 'Subiendo…' : <><strong className="text-brand-500">Haz clic para subir</strong> {helpText}</>}
          </p>
        </label>
      )}
      {error && (
        <p role="alert" aria-live="assertive" className="mt-2 text-xs text-error-500">
          {error}
        </p>
      )}
    </SectionHeader>
  );
}

function SendTicketsButton({ teamId }: { teamId: string }) {
  const mutation = useMutation({
    mutationFn: () => cccdAdmin.sendTeamTickets(teamId),
    onSuccess: ({ sent, skipped }) => {
      if (sent === 0) {
        toastError('No hay boletos con correo registrado para enviar. Verifica que el evento tenga los boletos activados y que los integrantes tengan correo.');
        return;
      }
      toastSuccess(
        skipped > 0
          ? `${sent} boleto(s) enviado(s). ${skipped} omitido(s) por no tener correo registrado.`
          : `${sent} boleto(s) enviado(s) por correo.`,
      );
    },
    onError: (err) => toastError(getValidationErrorMessage(err)),
  });

  return (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
      className={btnSecondary}
    >
      {mutation.isPending ? 'Enviando…' : 'Enviar boletos por correo'}
    </button>
  );
}

function TeamInfoForm({ team, onSaved }: { team: AdminTeamDetail; onSaved: () => void }) {
  const [name, setName] = useState(team.name);
  const [institutionId, setInstitutionId] = useState(team.institutionId ?? '');
  const [institutions, setInstitutions] = useState<InstitutionOption[]>([]);
  const [motor, setMotor] = useState(team.motor ?? '');
  const [motorOptions, setMotorOptions] = useState<CatalogOption[]>([]);
  const [categoryId, setCategoryId] = useState(team.categoryId ?? '');
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [error, setError] = useState('');
  const [savingContactId, setSavingContactId] = useState<string | null>(null);
  const [regenerateCredential, setRegenerateCredential] = useState(false);
  const primaryCount = team.members.filter((m) => m.isPrimaryContact).length;

  useEffect(() => {
    setName(team.name);
    setInstitutionId(team.institutionId ?? '');
    setMotor(team.motor ?? '');
    setCategoryId(team.categoryId ?? '');
    setRegenerateCredential(false);
  }, [team]);

  // cccd-team-rename-seedbed-sync: el equipo ya está vinculado a un grupo de
  // semillero (creado por enrollTeamInSeedbed) — un rename lo sincroniza
  // siempre; el código de acceso solo se regenera si el admin marca la
  // casilla. El año se lee del nombre actual del grupo ("<equipo> (<año>)")
  // en vez de traer eventStartDate solo para esto.
  const trimmedName = name.trim();
  const nameChanged = trimmedName.length > 0 && trimmedName !== team.name;
  const linkedGroup = team.seedbedGroup;
  const groupYearMatch = linkedGroup?.name.match(/\((\d{4})\)$/);
  const proposedGroupName = groupYearMatch ? `${trimmedName} (${groupYearMatch[1]})` : null;
  const eventName = team.event?.name ?? team.regionalEvent?.name ?? '';
  const proposedLoginPrefix = `${slugify(eventName)}-${slugify(trimmedName)}`.slice(0, 40);

  useEffect(() => {
    api
      .get('/academic-institutions/admin?limit=200&page=1')
      .then((res) => setInstitutions(res.data?.data?.institutions ?? []))
      .catch(() => setInstitutions([]));
  }, []);

  // El motor no se filtra por categoría: la misma categoría suele ofrecer
  // 2+ opciones (p. ej. primera vs. segunda participación) y cada equipo
  // elige la suya — ver reporte de usuario sobre este campo.
  useEffect(() => {
    cccdAdmin.listCatalogOptions('motor').then((res) => setMotorOptions(res)).catch(() => setMotorOptions([]));
  }, []);

  // cccd-team-category-change: categorías activas del mismo evento/regional
  // del equipo — el admin solo puede mover el equipo dentro de ese ámbito.
  useEffect(() => {
    const fetchCategories = team.event?.id
      ? cccdAdmin.getEventCategories(team.event.id)
      : team.regionalEvent?.id
        ? cccdAdmin.getRegionalCategories(team.regionalEvent.id)
        : null;
    fetchCategories?.then((res) => setCategories(res.filter((c) => c.isActive))).catch(() => setCategories([]));
  }, [team.event?.id, team.regionalEvent?.id]);

  const saveMutation = useMutation({
    mutationFn: () => cccdAdmin.updateTeam(team.id, {
      name,
      institutionId: institutionId || null,
      motor: motor || null,
      categoryId: categoryId || undefined,
      regenerateSeedbedCredential: nameChanged && linkedGroup?.hasActiveCredential ? regenerateCredential : false,
    }),
    onSuccess: (updated) => {
      if (updated.seedbedSync?.credentialRegenerated) {
        toastSuccess('Nombre y código de acceso del grupo de semillero actualizados — comparte el nuevo código con el equipo.');
      } else if (updated.seedbedSync?.synced) {
        toastSuccess('Nombre del grupo de semillero sincronizado.');
      }
      // cccd-team-category-change: no bloquea el guardado, solo informa qué
      // quedó inconsistente con la categoría nueva para que el staff lo revise.
      if (updated.categoryChangeWarnings?.length) {
        toast.warning(`Categoría cambiada con advertencias: ${updated.categoryChangeWarnings.join(' · ')}`);
      }
      onSaved();
    },
    onError: (err) => {
      const message = getValidationErrorMessage(err);
      setError(message);
      toastError(message);
    },
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); setError(''); saveMutation.mutate(); }} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionHeader
          title="Categoría"
          subtitle="Cambiarla no afecta miembros, pagos ni resultados ya registrados"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22V4a2 2 0 0 1 2-2h9l5 5v15" /><path d="M15 2v5h5" /></svg>}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="team-info-category" className={labelCls}>Categoría</label>
              <StyledSelect id="team-info-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {!categories.some((c) => c.slug === categoryId) && (
                  <option value={categoryId}>{categoryId || 'Sin asignar'}</option>
                )}
                {categories.map((c) => (
                  <option key={c.id} value={c.slug}>{c.name}</option>
                ))}
              </StyledSelect>
            </div>
            <div>
              <label htmlFor="team-info-motor" className={labelCls}>Motor</label>
              <StyledSelect id="team-info-motor" value={motor} onChange={(e) => setMotor(e.target.value)}>
                <option value="">Sin asignar</option>
                {motorOptions.map((opt) => (
                  <option key={opt.id} value={opt.value}>{opt.label}</option>
                ))}
              </StyledSelect>
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Equipo"
          subtitle="Nombre e institución de origen"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c1-4 4-6 9-6s8 2 9 6" /><circle cx="12" cy="7" r="4" /></svg>}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="team-info-name" className={labelCls}>Nombre del equipo *</label>
              <input id="team-info-name" required className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label htmlFor="team-info-institution" className={labelCls}>Institución académica</label>
              <StyledSelect id="team-info-institution" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)}>
                <option value="">Sin institución</option>
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>{inst.name}</option>
                ))}
              </StyledSelect>
            </div>
          </div>

          {linkedGroup && nameChanged && (
            // Aviso inline (sin modal, mismo patrón que la confirmación de
            // cambio de estado en TeamQuickActions): el equipo ya está
            // vinculado a un grupo de semillero — el nombre se sincroniza
            // siempre; el código de acceso solo si el admin lo pide.
            <div className="mt-3 rounded-xl border border-warning-300 bg-warning-50 p-3 dark:border-warning-800 dark:bg-warning-500/10">
              <p className="text-sm text-warning-800 dark:text-warning-300">
                Este equipo está vinculado al grupo de semillero{' '}
                <span className="font-semibold">"{linkedGroup.name}"</span>
                {proposedGroupName && <>, que se renombrará a <span className="font-semibold">"{proposedGroupName}"</span></>}.
              </p>
              {linkedGroup.hasActiveCredential ? (
                <label className="mt-2 flex items-start gap-2 text-sm text-warning-800 dark:text-warning-300">
                  <input
                    type="checkbox"
                    checked={regenerateCredential}
                    onChange={(e) => setRegenerateCredential(e.target.checked)}
                    className="mt-0.5 size-4 rounded border-warning-400"
                  />
                  <span>
                    Regenerar también el código de acceso del equipo (código actual: <code className="font-mono">{proposedLoginPrefix}-XXXXXX</code>). El código anterior dejará de funcionar — deberás compartir el nuevo con el equipo.
                  </span>
                </label>
              ) : (
                <p className="mt-1 text-xs text-warning-700 dark:text-warning-400">
                  Este grupo no tiene un código de acceso emitido todavía — solo se sincronizará el nombre.
                </p>
              )}
            </div>
          )}
        </SectionHeader>

        <SectionHeader
          title="Responsable"
          subtitle="Contacto principal — elegido de los miembros, máx. 2"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 20c1.5-4 5-6 8-6s6.5 2 8 6" /></svg>}
        >
          {team.members.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Sin miembros registrados todavía — el responsable se elige una vez alguien acepte una invitación.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {team.members.map((m) => {
                const disabled = savingContactId === m.id || (!m.isPrimaryContact && primaryCount >= 2);
                return (
                  <label
                    key={m.id}
                    title={disabled && !m.isPrimaryContact ? 'Este equipo ya tiene 2 contactos principales' : undefined}
                    className={`flex items-center justify-between gap-3 rounded-[10px] border border-gray-100 px-3.5 py-2.5 dark:border-gray-800 ${disabled && !m.isPrimaryContact ? 'opacity-60' : 'cursor-pointer'}`}
                  >
                    <span>
                      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{m.firstName} {m.lastName}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{m.email}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={m.isPrimaryContact}
                      disabled={disabled}
                      onChange={async (e) => {
                        setSavingContactId(m.id);
                        try {
                          await cccdAdmin.setMemberContact(team.id, m.id, e.target.checked);
                          onSaved();
                        } finally {
                          setSavingContactId(null);
                        }
                      }}
                      className="size-4 rounded border-gray-300"
                    />
                  </label>
                );
              })}
            </div>
          )}
        </SectionHeader>
      </div>

      {error && <Alert variant="error" title="No se pudo guardar" message={error} />}
      <div className="flex justify-end">
        <button type="submit" disabled={saveMutation.isPending} className={btnSave}>
          {saveMutation.isPending ? 'Guardando...' : 'Guardar información'}
        </button>
      </div>
    </form>
  );
}

export default function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // La pestaña vive en la URL (?tab=payments): se puede enlazar y sobrevive a recargas y a volver desde otra pantalla.
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_KEYS: Tab[] = ['info', 'members', 'payments', 'negotiation', 'ficha', 'audit'];
  const tabParam = searchParams.get('tab');
  const [tab, setTabState] = useState<Tab>(TAB_KEYS.includes(tabParam as Tab) ? (tabParam as Tab) : 'info');
  const setTab = (next: Tab) => {
    setTabState(next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    }, { replace: true });
  };
  const [reviewingPaymentId, setReviewingPaymentId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewAction, setReviewAction] = useState<'confirm' | 'reject' | null>(null);
  const [reviewError, setReviewError] = useState('');
  const [savingContactId, setSavingContactId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<AdminTeamMember | null>(null);
  const [uploadingPhotoMemberId, setUploadingPhotoMemberId] = useState<string | null>(null);

  async function handleRemoveMember(member: AdminTeamMember) {
    if (!team) return;
    setRemovingMemberId(member.id);
    try {
      await cccdAdmin.removeMember(team.id, member.id);
      toastSuccess('Miembro quitado del equipo.');
      refresh();
    } catch (err) {
      toastError(getValidationErrorMessage(err));
    } finally {
      setRemovingMemberId(null);
      setMemberToRemove(null);
    }
  }

  const { data: team, isLoading } = useQuery({
    queryKey: ['team', id],
    queryFn: () => cccdAdmin.getTeam(id!),
    enabled: !!id,
  });

  const teamKey = ['team', id];
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);

  const publicProfileMutation = useMutation({
    mutationFn: (enabled: boolean) => cccdAdmin.setTeamPublicProfile(id!, enabled),
    onMutate: (enabled) =>
      applyOptimistic<AdminTeamDetail>(qc, teamKey, (prev) => (prev ? { ...prev, publicProfileEnabled: enabled } : prev)),
    onError: (err, _enabled, previous) => {
      rollbackOptimistic(qc, teamKey, previous);
      toastError(getValidationErrorMessage(err));
    },
    onSuccess: (_d, enabled) => {
      toastSuccess(enabled ? 'Equipo publicado en el sitio público.' : 'Equipo retirado del sitio público.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: teamKey }),
  });

  const publicPhotoMutation = useMutation({
    mutationFn: ({ memberId, authorized }: { memberId: string; authorized: boolean }) =>
      cccdAdmin.setMemberPublicPhoto(id!, memberId, authorized),
    onMutate: ({ memberId, authorized }) =>
      applyOptimistic<AdminTeamDetail>(qc, teamKey, (prev) =>
        prev ? { ...prev, members: prev.members.map((m) => (m.id === memberId ? { ...m, publicPhotoAuthorized: authorized } : m)) } : prev,
      ),
    onError: (err, _vars, previous) => {
      rollbackOptimistic(qc, teamKey, previous);
      toastError(getValidationErrorMessage(err));
    },
    onSuccess: (_d, { authorized }) => {
      toastSuccess(authorized ? 'Foto autorizada para el sitio público.' : 'Autorización de foto pública retirada.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: teamKey }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['team', id] });
    qc.invalidateQueries({ queryKey: ['teams'] });
  };

  function onCancelReview() {
    setReviewingPaymentId(null);
    setRejectReason('');
    setReviewError('');
  }

  async function onConfirmPayment(payment: AdminPayment) {
    setReviewLoading(true);
    setReviewAction('confirm');
    setReviewError('');
    try {
      await cccdAdmin.confirmPayment(payment.id);
      onCancelReview();
      refresh();
    } catch (err) {
      const message = `No se pudo confirmar el pago. ${getErrorMessage(err).replace(/[.\s]+$/, '')}. Revisa el estado del pago antes de volver a intentarlo.`;
      setReviewError(message);
      toastError(message);
    } finally {
      setReviewLoading(false);
    }
  }

  async function onRejectPayment(payment: AdminPayment) {
    setReviewLoading(true);
    setReviewAction('reject');
    setReviewError('');
    try {
      await cccdAdmin.rejectPayment(payment.id, rejectReason);
      onCancelReview();
      refresh();
    } catch (err) {
      const message = `No se pudo rechazar el pago. ${getErrorMessage(err).replace(/[.\s]+$/, '')}. Revisa el estado del pago antes de volver a intentarlo.`;
      setReviewError(message);
      toastError(message);
    } finally {
      setReviewLoading(false);
    }
  }

  const pendingPaymentCount = team?.payments.filter((p) => p.status === 'pending').length ?? 0;

  const tabLabels = team
    ? {
        members: `Miembros (${team.members.length})`,
        payments: pendingPaymentCount > 0 ? `Pagos (${team.payments.length}) · ${pendingPaymentCount} por revisar` : `Pagos (${team.payments.length})`,
      }
    : undefined;

  const memberColumns: DataTableColumn<AdminTeamMember>[] = [
    {
      key: 'photo',
      label: 'Foto',
      align: 'center',
      render: (m) => {
        const isUploading = uploadingPhotoMemberId === m.id;
        return (
          <div className="flex items-center justify-center">
            {m.photoImageUrl ? (
              <button
                type="button"
                aria-label={`Quitar foto de ${m.firstName ?? m.email}`}
                title="Quitar foto"
                disabled={isUploading}
                onClick={async () => {
                  if (!team) return;
                  setUploadingPhotoMemberId(m.id);
                  try {
                    await cccdAdmin.updateMemberPhoto(team.id, m.id, null);
                    toastSuccess('Foto eliminada.');
                    refresh();
                  } catch (err) {
                    toastError(getValidationErrorMessage(err));
                  } finally {
                    setUploadingPhotoMemberId(null);
                  }
                }}
                className="flex min-h-11 min-w-11 items-center justify-center"
              >
                <img src={m.photoImageUrl} alt="" className="size-8 rounded-full border border-gray-200 object-cover dark:border-gray-800" />
              </button>
            ) : (
              <label className={`flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full border border-dashed border-gray-300 text-[11px] text-gray-400 dark:border-gray-700 ${isUploading ? 'opacity-60' : ''}`}>
                <span className="sr-only">Agregar foto de {m.firstName ?? m.email}</span>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={isUploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file || !team) return;
                    setUploadingPhotoMemberId(m.id);
                    try {
                      const { upload_url, key } = await cccdAdmin.getMemberPhotoUploadUrl(team.id, m.id, {
                        filename: file.name,
                        content_type: file.type || 'image/jpeg',
                      });
                      const putRes = await fetch(upload_url, {
                        method: 'PUT',
                        headers: { 'Content-Type': file.type || 'image/jpeg' },
                        body: file,
                      });
                      if (!putRes.ok) throw new Error(`Error subiendo la imagen: ${putRes.status}`);
                      await cccdAdmin.updateMemberPhoto(team.id, m.id, key);
                      toastSuccess('Foto actualizada.');
                      refresh();
                    } catch (err) {
                      toastError(err instanceof Error ? err.message : 'Error al subir la foto');
                    } finally {
                      setUploadingPhotoMemberId(null);
                    }
                  }}
                />
                +
              </label>
            )}
          </div>
        );
      },
    },
    {
      key: 'name',
      label: 'Nombre',
      render: (m) => {
        const initials = `${m.firstName?.[0] ?? '?'}${m.lastName?.[0] ?? ''}`;
        return (
          <span className="flex items-center gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {initials}
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {m.firstName || m.lastName ? `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() : <span className="italic text-gray-500 dark:text-gray-400">Sin nombre</span>}
            </span>
          </span>
        );
      },
    },
    {
      key: 'publicPhoto',
      label: 'Foto pública',
      render: (m) =>
        m.photoImageUrl ? (
          <div className="min-w-56 max-w-72">
            <PublicSwitch
              checked={m.publicPhotoAuthorized === true}
              onChange={(authorized) => publicPhotoMutation.mutate({ memberId: m.id, authorized })}
              label="Autorizo foto pública"
              ariaLabel={`Autorizo foto pública de ${m.firstName ?? m.email}`}
              description="Confirma el consentimiento para publicar esta foto en la web. No se usa la autorización de imagen interna."
            />
          </div>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400">Sin foto</span>
        ),
    },
    { key: 'email', label: 'Email', render: (m) => <span className="text-gray-500 dark:text-gray-400">{m.email}</span> },
    { key: 'role', label: 'Rol', render: (m) => <span className="text-gray-600 dark:text-gray-400">{m.role}</span> },
    {
      key: 'inviteStatus',
      label: 'Estado invitación',
      render: (m) => (
        <span className={`font-medium ${inviteStatusClass(m.inviteStatus)}`}>
          {INVITE_STATUS_LABELS[m.inviteStatus] ?? m.inviteStatus}
        </span>
      ),
    },
    {
      key: 'ficha',
      label: 'Ficha',
      render: (m) => {
        const complete = Boolean(m.documentNumber && m.address && m.bloodType && m.birthDate);
        if (!m.userId) {
          return <span className="text-xs text-gray-500 dark:text-gray-500">Sin cuenta todavía</span>;
        }
        return complete ? (
          <button
            type="button"
            onClick={() => navigate(`/instructor/students/${m.userId}/perfil`)}
            className="text-xs font-medium text-success-600 hover:underline dark:text-success-500"
          >
            Completa
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate(`/instructor/students/${m.userId}/perfil`)}
            className="text-xs font-medium text-warning-600 hover:underline dark:text-orange-400"
          >
            Completar ficha
          </button>
        );
      },
    },
    { key: 'country', label: 'País', render: (m) => <span className="text-gray-600 dark:text-gray-400">{m.country}</span> },
    { key: 'institution', label: 'Institución', render: (m) => <span className="text-gray-600 dark:text-gray-400">{m.institution}</span> },
    {
      key: 'isPrimaryContact',
      label: 'Contacto principal',
      align: 'center',
      render: (m) => {
        const primaryCount = team?.members.filter((mm) => mm.isPrimaryContact).length ?? 0;
        const disabled = savingContactId === m.id || (!m.isPrimaryContact && primaryCount >= 2);
        return (
          <label className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center">
            <span className="sr-only">Contacto principal — {m.firstName ?? m.email}</span>
            <input
              type="checkbox"
              checked={m.isPrimaryContact}
              disabled={disabled}
              title={disabled && !m.isPrimaryContact ? 'Este equipo ya tiene 2 contactos principales' : undefined}
              onChange={async (e) => {
                if (!team) return;
                setSavingContactId(m.id);
                try {
                  await cccdAdmin.setMemberContact(team.id, m.id, e.target.checked);
                  refresh();
                } finally {
                  setSavingContactId(null);
                }
              }}
              className="size-4 rounded border-gray-300"
            />
          </label>
        );
      },
    },
    {
      key: 'remove',
      label: '',
      headerAriaLabel: 'Quitar',
      align: 'center',
      render: (m) => (
        <button
          type="button"
          disabled={removingMemberId === m.id}
          onClick={() => setMemberToRemove(m)}
          aria-label={`Quitar a ${m.firstName ?? m.email} del equipo`}
          className="min-h-11 text-xs font-medium text-error-600 hover:underline disabled:opacity-60 dark:text-error-500"
        >
          {removingMemberId === m.id ? '...' : 'Quitar'}
        </button>
      ),
    },
  ];

  const memberCount = team?.members.length ?? 0;
  const atCapacity = team?.maxTeamSize != null && memberCount >= team.maxTeamSize;

  return (
    <>
    <TeamShell
      mode="edit"
      activeTab={tab}
      onTabChange={setTab}
      tabLabels={tabLabels}
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, team?.name ?? '...']}
      title={team?.name ?? 'Cargando...'}
      subtitle={team ? (team.event?.name ?? team.regionalEvent?.name ?? 'Equipo CCCD') : 'Equipo CCCD'}
      headerActions={
        team ? (
          <>
            <TeamStatusBadge status={team.status} />
            {pendingPaymentCount > 0 ? (
              <span className="text-xs font-medium text-warning-600 dark:text-orange-400">
                {pendingPaymentCount === 1 ? '1 pago pendiente de revisión' : `${pendingPaymentCount} pagos pendientes de revisión`}
              </span>
            ) : null}
            {tab === 'members' && <TourButton steps={cccdTeamRosterSyncTourSteps} label="Ver tour: cupo y sync" />}
          </>
        ) : undefined
      }
    >
      {isLoading ? (
        <LoadState />
      ) : !team ? (
        <Alert variant="error" title="Equipo no encontrado" message="No se encontró el equipo solicitado." />
      ) : (
        <>
      {/* Tab: Info */}
      {tab === 'info' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TeamImageUpload
              title="Parche del equipo"
              subtitle="Emblema/logo del equipo — se muestra en la galería de Competidores de Academy"
              imageUrl={team.patchImageUrl}
              shape="circle"
              uploadUrl={(file) => cccdAdmin.getTeamPatchUploadUrl(team.id, { filename: file.name, content_type: file.type || 'image/png' })}
              save={(key) => cccdAdmin.updateTeamPatch(team.id, key).then(() => refresh())}
              successLabel="Parche del equipo actualizado."
              removedLabel="Parche del equipo eliminado."
              helpText="el parche del equipo"
            />
            <TeamImageUpload
              title="Foto grupal del equipo"
              subtitle="Fotografía de todos los integrantes juntos — distinta del parche, también visible en Competidores"
              imageUrl={team.groupPhotoUrl}
              shape="landscape"
              uploadUrl={(file) => cccdAdmin.getTeamGroupPhotoUploadUrl(team.id, { filename: file.name, content_type: file.type || 'image/jpeg' })}
              save={(key) => cccdAdmin.updateTeamGroupPhoto(team.id, key).then(() => refresh())}
              successLabel="Foto grupal actualizada."
              removedLabel="Foto grupal eliminada."
              helpText="la foto grupal del equipo"
            />
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <PublicSwitch
              checked={team.publicProfileEnabled === true}
              disabled={team.status !== 'active'}
              busy={publicProfileMutation.isPending}
              onChange={(enabled) => (enabled ? setConfirmPublishOpen(true) : publicProfileMutation.mutate(false))}
              label="Publicar equipo en el sitio público"
              description="Visible en cccd.space/equipos solo si el equipo está activo. Confirma antes que el equipo autorizó su publicación."
            />
            {team.status !== 'active' && (
              <p className="mt-2 text-xs text-warning-600 dark:text-orange-400">
                Disponible solo cuando el equipo está activo (estado actual: {TEAM_STATUS_LABELS[team.status] ?? team.status}).
              </p>
            )}
          </div>
          <TeamInfoForm team={team} onSaved={refresh} />
        </div>
      )}

      {/* Tab: Members */}
      {tab === 'members' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            {team.maxTeamSize != null ? (
              <span
                data-tour="team-roster-cupo"
                className={`text-xs font-medium ${atCapacity ? 'text-warning-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}
              >
                {memberCount} / {team.maxTeamSize} miembros
              </span>
            ) : (
              <span data-tour="team-roster-cupo" className="text-xs text-gray-500 dark:text-gray-400">Sin cupo configurado en la categoría</span>
            )}
            <div data-tour="team-add-member">
              <AddMemberSearchBar
                teamId={team.id}
                onAdded={refresh}
                disabled={atCapacity}
                disabledReason={team.maxTeamSize != null ? `Este equipo ya alcanzó el cupo máximo de ${team.maxTeamSize} miembros de su categoría.` : undefined}
              />
            </div>
          </div>
          <div data-tour="team-roster-table">
            <DataTable
              columns={memberColumns}
              rows={team.members}
              rowKey={(m) => m.id}
              emptyMessage="Sin miembros registrados."
              page={1}
              pageSize={Math.max(team.members.length, 1)}
              total={team.members.length}
              onPageChange={() => {}}
            />
          </div>
        </div>
      )}

      {/* Tab: Payments */}
      {tab === 'payments' && (
        <div className="space-y-3" data-tour="team-payments-tab">
          <div className="flex justify-end">
            <button type="button" onClick={() => navigate(`/cccd/teams/${team.id}/payments/new`)} className={`min-h-11 ${btnSave}`}>
              + Registrar pago manual
            </button>
          </div>
          {team.payments.map((p) => (
            <div key={p.id} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCop(p.amount)} — {paymentMethodLabel(p.paymentMethod)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(p.createdAt).toLocaleString('es-CO')} · Estado:{' '}
                    <span className={`font-medium ${paymentStatusClass(p.status)}`}>
                      {PAYMENT_STATUS_LABELS[p.status] ?? p.status}
                    </span>
                  </p>
                  {p.bankReference && <p className="text-xs text-gray-500 dark:text-gray-400">Ref: {p.bankReference}</p>}
                </div>
                {p.status === 'pending' && reviewingPaymentId !== p.id && (
                  <button
                    type="button"
                    onClick={() => setReviewingPaymentId(p.id)}
                    className="min-h-11 rounded-lg bg-warning-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-warning-700"
                  >
                    Revisar
                  </button>
                )}
              </div>

              {reviewingPaymentId === p.id && (
                <div className="mt-3 space-y-2 rounded-lg border border-warning-200 bg-warning-50/60 p-3 dark:border-warning-800 dark:bg-warning-500/10">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Revisas un pago de <strong>{formatCop(p.amount)}</strong> de «{team.name}»
                    {p.bankReference ? ` (ref. ${p.bankReference})` : ''}. Confirmar lo registra como recibido; rechazar lo marca como no válido y guarda tu motivo.
                  </p>
                  {p.receiptUrl && (
                    <a href={p.receiptUrl} target="_blank" rel="noreferrer" className="block text-sm text-brand-600 underline">
                      Ver comprobante
                    </a>
                  )}
                  <label htmlFor={`reject-reason-${p.id}`} className="sr-only">Motivo de rechazo</label>
                  <textarea
                    id={`reject-reason-${p.id}`}
                    placeholder="Motivo de rechazo (obligatorio para rechazar)"
                    rows={2}
                    className={textareaCls}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  {reviewError && (
                    <Alert variant="error" title="No se pudo completar la revisión" message={reviewError} />
                  )}
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={onCancelReview} className={`min-h-11 ${btnSecondary}`}>
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => void onRejectPayment(p)}
                      disabled={reviewLoading || rejectReason.trim().length < 3}
                      title={rejectReason.trim().length < 3 ? 'Escribe el motivo para poder rechazar' : undefined}
                      className="min-h-11 rounded-xl bg-error-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-error-700 disabled:opacity-60"
                    >
                      {reviewLoading && reviewAction === 'reject' ? 'Rechazando...' : 'Rechazar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onConfirmPayment(p)}
                      disabled={reviewLoading}
                      className="min-h-11 rounded-xl bg-success-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-success-700 disabled:opacity-60"
                    >
                      {reviewLoading && reviewAction === 'confirm' ? 'Confirmando...' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {team.payments.length === 0 && <p className="text-gray-500 dark:text-gray-400 text-sm">Sin pagos registrados.</p>}
        </div>
      )}

      {/* Tab: Negotiation */}
      {tab === 'negotiation' && (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionHeader
              title="Descuento"
              subtitle="No acumulable — se congela tras el primer pago confirmado"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></svg>}
            >
              <TeamDiscountForm
                teamId={team.id}
                currentType={team.discountType as DiscountType}
                currentPercent={team.discountPercent}
                onSaved={refresh}
              />
              <div className="mt-3">
                <PodiumDiscountPicker teamId={team.id} onApplied={refresh} />
              </div>
            </SectionHeader>

            <div data-tour="team-status-actions">
              <SectionHeader
                title="Cambiar estado"
                subtitle={<>Estado actual: <strong className="text-brand-500">{TEAM_STATUS_LABELS[team.status] ?? team.status}</strong></>}
                icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22V3" /></svg>}
              >
                <TeamQuickActions
                  teamId={team.id}
                  currentStatus={team.status}
                  onStatusChanged={refresh}
                />
              </SectionHeader>
            </div>

            <SectionHeader
              title="Boletos de acceso"
              subtitle="Envía el boleto/QR por correo cuando el equipo esté listo para recibirlo"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 8l9 6 9-6" /></svg>}
            >
              <SendTicketsButton teamId={team.id} />
            </SectionHeader>
          </div>

          <div className="w-full lg:w-80 lg:shrink-0">
            <SectionHeader
              title="Historial de estados"
              subtitle="Últimos cambios"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>}
            >
              {team.statusLogs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Sin historial.</p>
              ) : (
                <TeamStatusTimeline logs={team.statusLogs} limit={2} />
              )}
              <button type="button" onClick={() => setTab('audit')} className="mt-3.5 text-xs font-semibold text-brand-500 hover:underline">
                Ver todo en Auditoría →
              </button>
            </SectionHeader>
          </div>
        </div>
      )}

      {/* Tab: Ficha operativa */}
      {tab === 'ficha' && <TeamFichaForm team={team} onSaved={refresh} />}

      {/* Tab: Audit */}
      {tab === 'audit' && (
        <SectionHeader
          title="Auditoría"
          subtitle="Historial completo de cambios de estado"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>}
        >
          {team.statusLogs.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Sin historial.</p>
          ) : (
            <TeamStatusTimeline logs={team.statusLogs} />
          )}
        </SectionHeader>
      )}
        </>
      )}
    </TeamShell>
    <ConfirmDialog
      isOpen={confirmPublishOpen}
      title="¿Publicar este equipo en el sitio público?"
      message="Se hará público en cccd.space/equipos el nombre del equipo y el nombre y la inicial del apellido de cada integrante. Confirma que el equipo autorizó su publicación."
      confirmLabel="Publicar"
      danger={false}
      onConfirm={() => {
        setConfirmPublishOpen(false);
        publicProfileMutation.mutate(true);
      }}
      onCancel={() => setConfirmPublishOpen(false)}
    />
    <ConfirmDialog
      isOpen={memberToRemove !== null}
      title={`¿Quitar a ${memberToRemove?.firstName ?? memberToRemove?.email ?? ''} del equipo?`}
      message="Esta acción no se puede deshacer."
      confirmLabel="Quitar"
      confirming={removingMemberId === memberToRemove?.id}
      onConfirm={() => memberToRemove && handleRemoveMember(memberToRemove)}
      onCancel={() => setMemberToRemove(null)}
    />
    </>
  );
}
